import { Dot } from "lucide-react";
import React, { useContext, useEffect, useRef, useState } from "react";
import Edit from "../../../../assets/images/icons/editIconNew.svg";
import DocumentViewer from "./DocumentViewer";
import MainServices from "../../../../services/MainServices";
import { notifyWarn } from "../../../../assets/elements/Toast";
import { toast } from "react-toastify";
import { AuthContext } from "../../../../context/AuthContext";
import { Field, FieldArray, Form, Formik } from "formik";
import * as Yup from "yup";
import CustomErrorMessage from "../../../../assets/common/CustomeErrorMessage";
import ButtonLoading from "../../../../assets/loaders/ButtonLoading";
import PDF from "./svg/Pdf";
import dateIcon from "../../../../assets/images/dateIcon.svg";
import useRTLContext from "../../../../hooks/useRTLContext";
import { useTranslation } from "react-i18next";
import { documentValidation } from "../employeesModals/validation";

const NationalID = ({
  nationalDetails,
  personalInfo,
  handleGetTableData,
  empPublicId,
  getEmployeeRegistrationData,
}) => {
  const { isRTL, direction } = useRTLContext();
  const { t, i18n } = useTranslation();
  const [documentViewerOpen, setDocumentViewerOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const fileInputRef1 = useRef(null);
  const fileInputRef2 = useRef(null);
  const [fileNames, setFileNames] = useState({
    frontDocument: nationalDetails?.nationalDocFront || "",
    backDocument: nationalDetails?.nationalDocBack || "",
  });
  const [editingSections, setEditingSections] = useState({
    nationalId: false,
    documents: false,
  });
  const [loading, setLoading] = useState(false);
  const [nationalDetailsError, setNationalDetailsError] = useState(null);
  const {
    updateIqamaInfo,
    fetchIqamaInfoById,
    fileUpload,
    updateEmployeeRegistrationMaster,
  } = MainServices();

  const handleDocumentChange = (e, setFieldValue) => {
    const { name, files } = e.target;

    if (files && files[0]) {
      const fileData = {
        file: files[0],
        fileName: files[0].name,
      };

      setFileNames((prev) => ({
        ...prev,
        [name]: fileData,
      }));

      const formikField =
        name === "frontDocument" ? "nationalDocFront" : "nationalDocBack";
      setFieldValue(formikField, fileData);

      console.log("File uploaded:", files[0].name);
    } else {
      console.log("No file selected");
    }
  };

  const handleRemoveDocument = (documentType, setFieldValue) => {
    setFileNames((prev) => ({
      ...prev,
      [documentType]: "",
    }));

    if (documentType === "frontDocument") {
      fileInputRef1.current.value = "";
      setFieldValue("nationalDocFront", "");
    } else if (documentType === "backDocument") {
      fileInputRef2.current.value = "";
      setFieldValue("nationalDocBack", "");
    }
  };

  console.log("fileNames: , ", fileNames);

  const handleViewDocument = (documentUrl, documentType, documentLabel) => {
    setSelectedDocument({
      url: documentUrl,
      type: getFileType(documentUrl),
      label: documentLabel,
    });
    setDocumentViewerOpen(true);
  };

  console.log("iqama details: " + nationalDetails);

  const getFileType = (url) => {
    try {
      const urlObj = new URL(url); // Ensure it's a valid URL
      const pathname = urlObj.pathname;
      const extension = pathname.split(".").pop().toLowerCase();

      return extension || "unknown"; // Return "unknown" if no extension found
    } catch (error) {
      return "invalid URL"; // Handle invalid URLs
    }
  };

  const handleSubmit = async (values) => {
    const updatedNationalIdInfo = {
      ...(values?.nationalId && {
        nationalId: values.nationalId,
      }),
      ...(values?.nationalIdExpiry && {
        nationalIdExpiry: values.nationalIdExpiry,
      }),
      ...(values?.nationalIdDob && {
        nationalIdDob: values.nationalIdDob,
      }),
      ...(values?.placeOfBirth && {
        placeOfBirth: values.placeOfBirth,
      }),
    };

    setLoading(true);
    let formattedPersonalInfo = {
      ...(personalInfo?.firstName && { firstName: personalInfo.firstName }),
      ...(personalInfo?.secondName && { secondName: personalInfo.secondName }),
      ...(personalInfo?.thirdName && { thirdName: personalInfo.thirdName }),
      ...(personalInfo?.lastName && { lastName: personalInfo.lastName }),
      ...(personalInfo?.phone && { phone: personalInfo.phone }),
      ...(personalInfo?.email && { email: personalInfo.email }),
      ...(personalInfo?.dobHijri && { dobHijri: personalInfo.dobHijri }),
      ...(personalInfo?.dobGregorian && {
        dobGregorian: personalInfo.dobGregorian,
      }),
      ...(personalInfo?.religion && {
        religion: personalInfo.religion,
      }),
      ...(personalInfo?.maritalStatus && {
        maritalStatus: personalInfo.maritalStatus,
      }),
      ...(personalInfo?.gender && { gender: personalInfo.gender }),
      ...(personalInfo?.citizen && { citizen: personalInfo.citizen }),

      ...(personalInfo?.citizen === "false" &&
        personalInfo?.nationality && {
          nationality: personalInfo?.nationality,
        }),
      ...(personalInfo?.citizen === "true" &&
        personalInfo?.nationalId && {
          nationalId: personalInfo.nationalId,
        }),
    };
    const details = {
      ...(formattedPersonalInfo && formattedPersonalInfo),
      documents: {
        nationalIdInfo: updatedNationalIdInfo,
      },
    };
    try {
      const response = await updateEmployeeRegistrationMaster(
        details,
        personalInfo?.publicId,
        personalInfo?.concurrencyStamp
      );

      if (response?.data) {
        handleEditToggle("nationalId");
        setNationalDetailsError(null);
        toast("National ID Details Updated Successfully!", {
          style: { zIndex: 9999 },
        });
        getEmployeeRegistrationData(empPublicId);
        handleGetTableData();
      } else {
        toast.error("Failed to update designation", {
          style: { zIndex: 9999 },
        });
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "An unexpected error occurred",
        {
          style: { zIndex: 9999 },
        }
      );
      console.error("Error", error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateDocuments = async () => {
    if (!nationalDetails?.nationalIdExpiry || !nationalDetails?.nationalId) {
      setNationalDetailsError(
        "Please complete all required National ID details before submitting documents"
      );
      return;
    } else {
      setNationalDetailsError(null); // Clear error if details are complete
    }

    const files = [];

    // Add files only if they exist
    if (fileNames?.frontDocument?.file) {
      files.push(fileNames.frontDocument.file);
    }
    if (fileNames?.backDocument?.file) {
      files.push(fileNames.backDocument.file);
    }

    try {
      let updatedFileNames = {};

      // If there are files to upload, call the fileUpload API
      if (files.length > 0) {
        const response = await fileUpload(files);
        if (response?.data) {
          const responseData = response?.data?.data;
          let currentIndex = 0;

          // Handle front document
          if (fileNames?.frontDocument?.file) {
            updatedFileNames.frontDocument =
              responseData[currentIndex]?.fileUrl;
            currentIndex++;
          } else {
            updatedFileNames.frontDocument = fileNames.frontDocument || ""; // Use existing value or empty
          }

          // Handle back document
          if (fileNames?.backDocument?.file) {
            updatedFileNames.backDocument = responseData[currentIndex]?.fileUrl;
          } else {
            updatedFileNames.backDocument = fileNames.backDocument || ""; // Use existing value or empty
          }
        } else {
          throw new Error(response?.message || "Unknown error");
        }
      } else {
        // If no files to upload, directly use the existing fileNames values
        updatedFileNames = {
          frontDocument: fileNames.frontDocument, // Use existing value or empty
          backDocument: fileNames.backDocument, // Use existing value or empty
        };
      }

      // Prepare the personal info object
      let formattedPersonalInfo = {
        ...(personalInfo?.firstName && { firstName: personalInfo.firstName }),
        ...(personalInfo?.secondName && {
          secondName: personalInfo.secondName,
        }),
        ...(personalInfo?.thirdName && { thirdName: personalInfo.thirdName }),
        ...(personalInfo?.lastName && { lastName: personalInfo.lastName }),
        ...(personalInfo?.phone && { phone: personalInfo.phone }),
        ...(personalInfo?.email && { email: personalInfo.email }),
        ...(personalInfo?.dobHijri && { dobHijri: personalInfo.dobHijri }),
        ...(personalInfo?.dobGregorian && {
          dobGregorian: personalInfo.dobGregorian,
        }),
        ...(personalInfo?.religion && { religion: personalInfo.religion }),
        ...(personalInfo?.maritalStatus && {
          maritalStatus: personalInfo.maritalStatus,
        }),
        ...(personalInfo?.gender && { gender: personalInfo.gender }),
        ...(personalInfo?.citizen && { citizen: personalInfo.citizen }),

        ...(personalInfo?.citizen === "false" &&
          personalInfo?.nationality && {
            nationality: personalInfo?.nationality,
          }),
        ...(personalInfo?.citizen === "true" &&
          personalInfo?.nationalId && {
            nationalId: personalInfo.nationalId,
          }),
      };

      // Prepare the updated national ID info
      const updatedNationalIdInfo = {
        nationalDocFront: updatedFileNames.frontDocument,
        nationalDocBack: updatedFileNames.backDocument,
      };

      // Prepare the final payload
      const details = {
        ...(formattedPersonalInfo && formattedPersonalInfo),
        documents: {
          nationalIdInfo: updatedNationalIdInfo,
        },
      };

      // Call the update API
      const updateResponse = await updateEmployeeRegistrationMaster(
        details,
        personalInfo?.publicId,
        personalInfo?.concurrencyStamp
      );

      if (updateResponse?.data) {
        toast("National ID Documents Updated Successfully!", {
          style: { zIndex: 999999999999999 },
        });
        getEmployeeRegistrationData(empPublicId);
        handleGetTableData();
        // Toggle back to "Edit" mode
        handleEditToggle("documents");
      }
    } catch (error) {
      console.error("Error updating national ID information:", error);
      notifyWarn(
        error?.response?.data?.message || "An unexpected error occurred"
      );
    } finally {
      setLoading(false);
    }
  };

  const getValidationSchema = (section) => {
    const baseSchema = {
      nationalId: Yup.string().required("National ID is required").trim(),
      nationalIdExpiry: Yup.date()
        .required("National ID Expiry is required")
        .typeError("Invalid date format"),
    };

    const documentSchema = {
      nationalDocFront: documentValidation(),
      nationalDocBack: documentValidation(),
    };

    if (section === "nationalId") {
      return Yup.object().shape(baseSchema);
    } else if (section === "documents") {
      return Yup.object().shape(documentSchema);
    } else {
      return Yup.object().shape({});
    }
  };

  const formatDateToYYYYMMDD = (date) => {
    if (!date) return "";

    const isISODate = /^\d{4}-\d{2}-\d{2}T/.test(date);
    const isDDMMYYYY = /^\d{2}\/\d{2}\/\d{4}$/.test(date);
    const isYYYYMMDD = /^\d{4}-\d{2}-\d{2}$/.test(date);

    let formattedDate = "";

    if (isISODate) {
      formattedDate = new Date(date).toISOString().split("T")[0];
    } else if (isDDMMYYYY) {
      const [day, month, year] = date.split("/");
      formattedDate = `${year}-${month.padStart(2, "0")}-${day.padStart(
        2,
        "0"
      )}`;
    } else if (isYYYYMMDD) {
      formattedDate = date;
    }

    return formattedDate;
  };

  function handleOpenFileInput() {
    fileInputRef1.current.click();
  }

  function handleOpenFileInput2() {
    fileInputRef2.current.click();
  }

  const formatDate = (dateString) => {
    if (!dateString) return ""; // Handle empty values
    const date = new Date(dateString);
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  const formatDateToDotFormat = (dateString) => {
    if (!dateString) return ""; // Handle empty or invalid input

    const date = new Date(dateString);

    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are 0-based
    const year = date.getFullYear();

    return `${day}.${month}.${year}`;
  };

  const formatDateForInput = (dateString) => {
    if (!dateString) return ""; // Handle empty or undefined values

    const date = new Date(dateString);

    // Convert to YYYY-MM-DD format
    return date.toISOString().split("T")[0];
  };

  const calculateExpiryDays = (dateString) => {
    if (!dateString) return null;
    const expiryDate = new Date(dateString);
    if (isNaN(expiryDate)) return null;
    const diffInMs = expiryDate - new Date();
    const diffInDays = Math.ceil(diffInMs / (1000 * 60 * 60 * 24));
    return diffInDays > 0 ? `Expiry In ${diffInDays} Days` : "Expired";
  };

  const handleEditToggle = (section, values) => {
    setEditingSections((prev) => {
      const isCurrentlyOpen = prev[section];
      const newState = {
        nationalId: false,
        documents: false,
      };

      // If the clicked section was previously closed, open it
      if (!isCurrentlyOpen) {
        newState[section] = true;
      }

      return newState;
    });
  };

  return (
    <>
      <div className="tab-section">
        {/* Iqama Section */}
        <div className="tab-header-container">
          <h3 className="tab-heading">{t("National ID")}</h3>
          {!editingSections.nationalId && ( // Show edit button only when not in edit mode
            <div
              className="tab-header-edit-container"
              onClick={() => handleEditToggle("nationalId")}
            >
              <img src={Edit} alt="edit icon" />
              <span className="edit-txt">{t("Edit")}</span>
            </div>
          )}
        </div>
        <Formik
          enableReinitialize
          initialValues={{
            nationalId: personalInfo?.nationalId || "",
            nationalIdExpiry: nationalDetails?.nationalIdExpiry || "",
            nationalIdDob: nationalDetails?.nationalIdDob || "",
            placeOfBirth: nationalDetails?.placeOfBirth || "",
            nationalDocFront: nationalDetails?.nationalDocFront || "",
            nationalDocBack: nationalDetails?.nationalDocBack || "",
          }}
          validationSchema={
            editingSections.nationalId
              ? getValidationSchema("nationalId")
              : editingSections.documents
              ? getValidationSchema("documents")
              : undefined
          }
          onSubmit={(values) => {
            setLoading(true);
            if (editingSections.nationalId) {
              handleSubmit(values).finally(() => setLoading(false));
            } else if (editingSections.documents) {
              handleUpdateDocuments(values).finally(() => setLoading(false));
            }
          }}
        >
          {({ values, errors, touched, setFieldValue }) => (
            <Form className="form-section">
              <div className="add-body-content-grid">
                {/* National ID */}
                <div className="form-group">
                  <div className="add-body-input-section">
                    <div className="add-label-container">
                      <label className="add-label" htmlFor="nationalId">
                        {t("National ID")}
                      </label>
                      <span className="add-req-mark">*</span>
                    </div>
                    {editingSections.nationalId ? (
                      <input
                        id="nationalId"
                        name="nationalId"
                        className="add-input"
                        placeholder={t("Enter National ID")}
                        value={values.nationalId}
                        onChange={(e) =>
                          setFieldValue("nationalId", e.target.value)
                        }
                      />
                    ) : (
                      <div className="display-value">
                        {values.nationalId || t("N/A")}
                      </div>
                    )}
                  </div>
                  <CustomErrorMessage
                    name="nationalId"
                    errors={errors}
                    touched={touched}
                  />
                </div>

                {/* National ID Expiry */}
                <div className="form-group">
                  <div className="add-body-input-section">
                    <div className="add-label-container">
                      <label className="add-label" htmlFor="nationalIdExpiry">
                        {t("National ID Expiry")}
                      </label>
                      <span className="add-req-mark">*</span>
                    </div>
                    {editingSections.nationalId ? (
                      <div className="add-input-wrapper">
                        <input
                          type="date"
                          id="nationalIdExpiry"
                          name="nationalIdExpiry"
                          className="add-wrap-input"
                          value={formatDateToYYYYMMDD(values.nationalIdExpiry)}
                          onChange={(e) =>
                            setFieldValue("nationalIdExpiry", e.target.value)
                          }
                        />
                        <img
                          src={dateIcon}
                          alt="date icon"
                          className="add-date-input-in"
                        />
                      </div>
                    ) : (
                      <div className="display-value">
                        {values.nationalIdExpiry
                          ? formatDateToYYYYMMDD(values.nationalIdExpiry)
                          : t("N/A")}
                      </div>
                    )}
                  </div>
                  <CustomErrorMessage
                    name="nationalIdExpiry"
                    errors={errors}
                    touched={touched}
                  />
                </div>

                {/* Date of Birth */}
                <div className="form-group">
                  <div className="add-body-input-section">
                    <div className="add-label-container">
                      <label className="add-label" htmlFor="nationalIdDob">
                        {t("Date of Birth")}
                      </label>
                    </div>
                    {editingSections.nationalId ? (
                      <div className="add-input-wrapper">
                        <input
                          type="date"
                          id="nationalIdDob"
                          name="nationalIdDob"
                          className="add-wrap-input"
                          value={formatDateToYYYYMMDD(values.nationalIdDob)}
                          onChange={(e) =>
                            setFieldValue("nationalIdDob", e.target.value)
                          }
                        />
                        <img
                          src={dateIcon}
                          alt="date icon"
                          className="add-date-input-in"
                        />
                      </div>
                    ) : (
                      <div className="display-value">
                        {values.nationalIdDob
                          ? formatDateToYYYYMMDD(values.nationalIdDob)
                          : t("N/A")}
                      </div>
                    )}
                  </div>
                  <CustomErrorMessage
                    name="nationalIdDob"
                    errors={errors}
                    touched={touched}
                  />
                </div>

                {/* Place of Birth */}
                <div className="form-group">
                  <div className="add-body-input-section">
                    <div className="add-label-container">
                      <label className="add-label" htmlFor="placeOfBirth">
                        {t("Place of Birth")}
                      </label>
                    </div>
                    {editingSections.nationalId ? (
                      <input
                        id="placeOfBirth"
                        name="placeOfBirth"
                        className="add-input"
                        placeholder={t("Enter Place of Birth")}
                        value={values.placeOfBirth}
                        onChange={(e) =>
                          setFieldValue("placeOfBirth", e.target.value)
                        }
                      />
                    ) : (
                      <div className="display-value">
                        {values.placeOfBirth || t("N/A")}
                      </div>
                    )}
                  </div>
                  <CustomErrorMessage
                    name="placeOfBirth"
                    errors={errors}
                    touched={touched}
                  />
                </div>
              </div>

              {/* Submit button for National ID section */}
              {editingSections.nationalId && (
                <div className="add-footer">
                  <span></span>
                  <div className="add-footer-right">
                    <button
                      type="button"
                      className="add-footer-cancel-btn"
                      onClick={() => handleEditToggle("nationalId")}
                    >
                      {t("Cancel")}
                    </button>
                    <button
                      type="submit"
                      className="add-footer-submit-btn"
                      disabled={loading}
                    >
                      {loading ? <ButtonLoading /> : t("Submit")}
                    </button>
                  </div>
                </div>
              )}

              {/* Documents Section */}
              <div className="tab-header-container">
                <h3 className="tab-heading">{t("Documents")}</h3>
                {!editingSections.documents && (
                  <div
                    className="tab-header-edit-container"
                    onClick={() => handleEditToggle("documents")}
                  >
                    <img src={Edit} alt="edit icon" />
                    <span className="edit-txt">{t("Edit")}</span>
                  </div>
                )}
              </div>

              {/* Documents Section Body */}
              {editingSections.documents ? (
                // Edit Mode
                <div className="upload-body-tab">
                  {nationalDetailsError && (
                    <div className="details-error">{nationalDetailsError}</div>
                  )}
                  {/* Front Document */}
                  <div className="upload-file-input-container m-0">
                    <div className="upload-file-input-container2">
                      <div className="upload-label-container">
                        <label
                          htmlFor="nationalDocFront"
                          className="upload-label"
                        >
                          {t("Front Document")}
                        </label>
                        <span className="upload-req-mark">*</span>
                      </div>
                      <div className="remove-btn-wrapper">
                        <div
                          className="upload-file-input-wrapper"
                          onClick={handleOpenFileInput}
                        >
                          <span className="upload-file-input-replacer">
                            {fileNames?.frontDocument?.fileName ||
                              fileNames?.frontDocument ||
                              t("No Document")}
                          </span>
                          <input
                            name="frontDocument"
                            ref={fileInputRef1}
                            id="nationalDocFront"
                            type="file"
                            className="upload-file-hidden-input"
                            onChange={(e) =>
                              handleDocumentChange(e, setFieldValue)
                            }
                          />

                          <button type="button" className="upload-btn">
                            {t("Upload File")}
                          </button>
                        </div>
                        {fileNames?.frontDocument && (
                          <button
                            type="button"
                            className="remove-file-btn-national"
                            onClick={() =>
                              handleRemoveDocument(
                                "frontDocument",
                                setFieldValue
                              )
                            }
                          >
                            {t("Remove")}
                          </button>
                        )}
                      </div>
                    </div>
                    <CustomErrorMessage
                      name="nationalDocFront"
                      errors={errors}
                      touched={touched}
                    />
                  </div>

                  {/* Back Document */}
                  <div className="upload-file-input-container m-0">
                    <div className="upload-file-input-container2">
                      <div className="upload-label-container">
                        <label
                          htmlFor="nationalDocBack"
                          className="upload-label"
                        >
                          {t("Back Document")}
                        </label>
                        <span className="upload-req-mark">*</span>
                      </div>
                      <div className="remove-btn-wrapper">
                        <div
                          className="upload-file-input-wrapper"
                          onClick={handleOpenFileInput2}
                        >
                          <span className="upload-file-input-replacer">
                            {fileNames?.backDocument?.fileName ||
                              fileNames?.backDocument ||
                              t("No Document")}
                          </span>
                          <input
                            name="backDocument"
                            ref={fileInputRef2}
                            id="nationalDocBack"
                            type="file"
                            className="upload-file-hidden-input"
                            onChange={(e) =>
                              handleDocumentChange(e, setFieldValue)
                            }
                          />
                          <button type="button" className="upload-btn">
                            {t("Upload File")}
                          </button>
                        </div>
                        {fileNames?.backDocument && (
                          <button
                            type="button"
                            className="remove-file-btn-national"
                            onClick={() =>
                              handleRemoveDocument(
                                "backDocument",
                                setFieldValue
                              )
                            }
                          >
                            {t("Remove")}
                          </button>
                        )}
                      </div>
                    </div>
                    <CustomErrorMessage
                      name="nationalDocBack"
                      errors={errors}
                      touched={touched}
                    />
                  </div>
                </div>
              ) : (
                // Non-Edit Mode
                <div className="upload-body-tab">
                  {/* Front Document */}
                  <div className="upload-file-input-container-tab">
                    <div className="upload-file-input-container2">
                      <div className="upload-label-container">
                        <label
                          htmlFor="nationalDocFront"
                          className="upload-label"
                        >
                          {t("Front Document")}
                        </label>
                      </div>
                      <div className="document-display-container-tab">
                        <div className="document-info-tab">
                          <PDF />
                          <span className="document-name-tab">
                            {values.nationalDocFront
                              ? t("Front Document")
                              : t("No Document Uploaded")}
                          </span>
                        </div>
                        {values.nationalDocFront && (
                          <button
                            type="button"
                            className="view-document-btn-tab"
                            onClick={() =>
                              handleViewDocument(
                                values.nationalDocFront,
                                "Front Document",
                                "National Front"
                              )
                            }
                          >
                            {t("View")}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Back Document */}
                  <div className="upload-file-input-container-tab">
                    <div className="upload-file-input-container2">
                      <div className="upload-label-container">
                        <label
                          htmlFor="nationalDocBack"
                          className="upload-label"
                        >
                          {t("Back Document")}
                        </label>
                      </div>
                      <div className="document-display-container-tab">
                        <div className="document-info-tab">
                          <PDF />
                          <span className="document-name-tab">
                            {values.nationalDocBack
                              ? t("Back Document")
                              : t("No Document Uploaded")}
                          </span>
                        </div>
                        {values.nationalDocBack && (
                          <button
                            type="button"
                            className="view-document-btn-tab"
                            onClick={() =>
                              handleViewDocument(
                                values.nationalDocBack,
                                "Back Document",
                                "National Back"
                              )
                            }
                          >
                            {t("View")}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Submit button for Documents section */}
              {editingSections.documents && (
                <div className="add-footer">
                  <span></span>
                  <div className="add-footer-right">
                    <button
                      type="button"
                      className="add-footer-cancel-btn"
                      onClick={() => handleEditToggle("documents")}
                    >
                      {t("Cancel")}
                    </button>
                    <button
                      type="submit"
                      className="add-footer-submit-btn"
                      disabled={loading}
                    >
                      {loading ? <ButtonLoading /> : t("Submit")}
                    </button>
                  </div>
                </div>
              )}
            </Form>
          )}
        </Formik>
      </div>

      <DocumentViewer
        isOpen={documentViewerOpen}
        onClose={() => setDocumentViewerOpen(false)}
        documentUrl={selectedDocument?.url}
        documentType={selectedDocument?.type}
        documentLabel={selectedDocument?.label}
      />
    </>
  );
};

export default NationalID;
